import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ClientDataFeedService, DataFeed, Client, Dashboard, UserInfo, FeedConnection } from '../../../services/datafeed-service';
import { DialogService } from '../../utility/dialog/dialog-service';
import { ModalType } from '../../utility/dialog-template/dialog-template.component';
import { HttpService } from '../../utility/http-service';
import { LegacyXrefDialogComponent } from './legacy-xref-editor/legacy-xref-dialog.component';

interface LocalUserInfo {
  isDeveloper: boolean;
}

/** Supported export types. Maps to DataFeed.ExportName / the IFeedExporter.Name values on the server. */
export const EXPORT_TYPES = ['JMA', 'Database', 'FileExport'] as const;
export const TRANSFER_FORMATS = ['CSV', 'XML', 'JSON'] as const;

/**
 * Working model holding every field across all export types. Only the fields
 * relevant to the selected export type are shown/serialized. Adding a new export
 * type means adding its fields here + a block in the template/serializer.
 */
export interface ExportPayloadModel {
  // JMA
  destEmails: string[];
  templatePath: string;
  // Database
  autoLoadSchema: boolean;
  // Database + FileExport
  connectionId: string;
  useLegacyIds: boolean;
  // FileExport
  transferFormat: string;
  partnerCode: string;
  useLegacySync: boolean;
  // FileExport, Direct Exchange (legacy) - CSV only
  useDirectExchange: boolean;
  tierDatabaseName: string;
  autoLoadXref: boolean;
  autoLoadRecipients: string[];
}

type EditableFeed = DataFeed & { editing?: boolean; payload?: ExportPayloadModel };

@Component({
  selector: 'app-client-datafeed',
  templateUrl: './client-datafeed.component.html',
  styleUrls: ['./client-datafeed.component.css'],
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule]
})
export class ClientDataFeedComponent implements OnInit, OnChanges {
  @Input() clientId: string | null = null;

  feeds: EditableFeed[] = [];
  loading = false;
  error: string | null = null;
  originalCopies: { [id: string]: DataFeed } = {};

  clients: Client[] = [];
  dashboards: Dashboard[] = [];
  users: UserInfo[] = [];

  exportTypes = EXPORT_TYPES;
  transferFormats = TRANSFER_FORMATS;
  dataConnections: FeedConnection[] = [];
  fileConnections: FeedConnection[] = [];

  // The Well Mappings dialog (LegacyXRef grid) is developer-gated, same as the sibling
  // "Data Sets" tab - it exposes cross-client well id mappings.
  isDeveloper = false;

  constructor(
    private feedService: ClientDataFeedService,
    private dialog: DialogService,
    private http: HttpService,
    private matDialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.loadAllClients();
    this.loadUserInfo();
    if (this.clientId) {
      this.loadFeeds();
      this.loadUsers();
    }
  }

  private loadUserInfo(): void {
    this.http.get<LocalUserInfo>('User/GetUserInfo', (res: LocalUserInfo) => {
      this.isDeveloper = !!res.isDeveloper;
    });
  }

  openMappings(feed: EditableFeed): void {
    if (!feed.id) return;
    this.matDialog.open(LegacyXrefDialogComponent, {
      data: { dataFeedId: feed.id, feedName: feed.name },
      minWidth: '80vw',
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clientId'] && this.clientId) {
      this.loadFeeds();
      this.loadUsers();
    }
  }

  private loadAllClients(): void {
    this.feedService.getAllClients(clients => this.clients = clients);
  }

  loadUsers(): void {
    if (!this.clientId) return;
    this.feedService.getUsers(this.clientId, users => this.users = users);
  }

  loadDashboards(sourceClientId: string): void {
    if (!sourceClientId) { this.dashboards = []; return; }
    this.feedService.getDashboards(sourceClientId, defs => this.dashboards = defs);
  }

  loadFeeds(): void {
    if (!this.clientId) return;
    this.loading = true;
    this.error = null;
    this.feedService.getFeeds(this.clientId, (feeds: DataFeed[]) => {
      this.feeds = feeds.map(f => ({
        ...f,
        editing: false,
        autoRun: !!f.autoRun,
        includeCalcs: !!f.includeCalcs,
        payload: this.parsePayload(f.exportPayload),
      }));
      this.loading = false;
    });
  }

  addFeed(): void {
    if (!this.clientId) { this.error = 'Client ID is missing.'; return; }
    if (this.feeds.some(f => !f.clientId && f.editing)) return;

    const newFeed: EditableFeed = {
      clientId: '',
      sourceClientId: '',
      autoRun: false,
      name: 'New Feed',
      executionUserId: '',
      includeCalcs: false,
      dashboardId: '',
      exportName: '',
      editing: true,
      payload: this.defaultPayload()
    };
    this.feeds.unshift(newFeed);
  }

  saveFeed(feed: EditableFeed): void {
    if (!this.clientId) { this.error = 'Client ID is missing.'; return; }

    feed.clientId = this.clientId;
    if (!feed.sourceClientId?.trim()) feed.sourceClientId = this.clientId;
    feed.dashboardId = feed.dashboardId?.trim() || '';
    feed.name = feed.name?.trim() || 'New Feed';

    const errors: string[] = [];
    if (!feed.name) errors.push('Name is required.');
    if (!feed.dashboardId) errors.push('Dashboard is required.');
    errors.push(...this.validateExport(feed));
    if (errors.length > 0) { this.error = errors.join(' '); return; }

    this.serializeExport(feed);

    this.error = null;
    const isNew = !feed.id;
    if (isNew) feed.id = crypto.randomUUID();

    const saveFn = isNew
      ? this.feedService.createFeed.bind(this.feedService)
      : this.feedService.updateFeed.bind(this.feedService);

    saveFn(feed, () => {
      this.loadFeeds();
      this.dialog.openModal('Success', `Feed "${feed.name}" saved successfully.`, ModalType.INFO);
    });
  }

  // The card header expands/collapses a feed. Expanding must run the same setup
  // as the Edit button (parse payload, load dashboards/connections) otherwise the
  // type-specific fields have no model and never render.
  onHeaderToggle(feed: EditableFeed): void {
    if (feed.editing) {
      feed.editing = false;
    } else if (feed.clientId) {
      this.editFeed(feed);
    } else {
      feed.editing = true;
    }
  }

  editFeed(feed: EditableFeed): void {
    this.feeds.forEach(f => { if (f !== feed) f.editing = false; });
    feed.editing = true;
    feed.payload = this.parsePayload(feed.exportPayload);
    // Deep clone so cancelling restores the nested payload object too.
    this.originalCopies[feed.id || 'new'] = JSON.parse(JSON.stringify({ ...feed, editing: false }));
    if (feed.sourceClientId) this.loadDashboards(feed.sourceClientId);
    this.loadConnectionsForType(feed.exportName);
  }

  cancelEdit(feed: DataFeed & { editing?: boolean }): void {
    const id = feed.id || 'new';
    if (!feed.id) {
      this.feeds = this.feeds.filter(f => f !== feed);
    } else if (this.originalCopies[id]) {
      Object.assign(feed, this.originalCopies[id]);
      delete this.originalCopies[id];
      feed.editing = false;
    } else {
      feed.editing = false;
    }
  }

  deleteFeed(feed: DataFeed): void {
    if (!this.clientId || !feed.id) return;
    this.dialog.openModal(
      'Confirm Deletion',
      `Are you sure you want to delete feed '${feed.name}'?`,
      ModalType.QUESTION,
      () => {
        this.feedService.deleteFeed(
          { clientId: this.clientId!, feedId: feed.id! },
          () => {
            this.feeds = this.feeds.filter(f => f.id !== feed.id);
            this.dialog.openModal('Deleted', `Feed '${feed.name}' has been deleted.`, ModalType.INFO);
          }
        );
      }
    );
  }

  get isEditingAny(): boolean {
    return this.feeds.some(f => f.editing);
  }

  onSourceClientChange(feed: EditableFeed): void {
    if (feed.sourceClientId) this.loadDashboards(feed.sourceClientId);
    else this.dashboards = [];
  }

  // ---- Export type handling ----

  private defaultPayload(): ExportPayloadModel {
    return {
      destEmails: [''],
      templatePath: '',
      autoLoadSchema: false,
      connectionId: '',
      // Legacy flags default to false in the UI even though the .NET options
      // classes default to true (kept true there for backwards compatibility).
      useLegacyIds: false,
      transferFormat: '',
      partnerCode: '',
      useLegacySync: false,
      useDirectExchange: false,
      tierDatabaseName: '',
      autoLoadXref: false,
      autoLoadRecipients: ['']
    };
  }

  private parsePayload(raw?: string): ExportPayloadModel {
    const model = this.defaultPayload();
    if (!raw) return model;
    try {
      const p = JSON.parse(raw) ?? {};
      if (Array.isArray(p.DestEmails) && p.DestEmails.length) model.destEmails = p.DestEmails;
      if (typeof p.TemplatePath === 'string') model.templatePath = p.TemplatePath;
      if (typeof p.AutoLoadSchema === 'boolean') model.autoLoadSchema = p.AutoLoadSchema;
      if (typeof p.ConnectionId === 'string') model.connectionId = p.ConnectionId;
      if (typeof p.UseLegacyIds === 'boolean') model.useLegacyIds = p.UseLegacyIds;
      if (typeof p.TransferFormat === 'string') model.transferFormat = p.TransferFormat;
      if (typeof p.PartnerCode === 'string') model.partnerCode = p.PartnerCode;
      if (typeof p.UseLegacySync === 'boolean') model.useLegacySync = p.UseLegacySync;
      if (typeof p.UseDirectExchange === 'boolean') model.useDirectExchange = p.UseDirectExchange;
      if (typeof p.TierDatabaseName === 'string') model.tierDatabaseName = p.TierDatabaseName;
      if (typeof p.AutoLoadXref === 'boolean') model.autoLoadXref = p.AutoLoadXref;
      if (Array.isArray(p.AutoLoadRecipients) && p.AutoLoadRecipients.length) model.autoLoadRecipients = p.AutoLoadRecipients;
    } catch {
      // malformed payload — fall back to defaults
    }
    return model;
  }

  onExportTypeChange(feed: EditableFeed): void {
    feed.payload = this.defaultPayload();
    this.loadConnectionsForType(feed.exportName);
  }

  private loadConnectionsForType(exportName?: string): void {
    if (!this.clientId) return;
    if (exportName === 'Database') {
      this.feedService.getConnections(this.clientId, 'Data', c => this.dataConnections = c);
    } else if (exportName === 'FileExport') {
      this.feedService.getConnections(this.clientId, 'File', c => this.fileConnections = c);
    }
  }

  private validateExport(feed: EditableFeed): string[] {
    const errors: string[] = [];
    const p = feed.payload;
    if (!feed.exportName) return errors; // no export configured — allowed
    if (!p) { errors.push('Export configuration is required.'); return errors; }

    switch (feed.exportName) {
      case 'JMA': {
        const emails = p.destEmails.map(e => e.trim()).filter(e => e.length > 0);
        if (emails.length === 0) errors.push('At least one destination email is required.');
        else if (!emails.every(e => this.isValidEmail(e))) errors.push('One or more destination emails are invalid.');
        if (!p.templatePath?.trim()) errors.push('Template path is required.');
        else if (!this.isValidWindowsPath(p.templatePath.trim())) errors.push('Template path must be a valid Windows path.');
        break;
      }
      case 'Database':
        if (!p.connectionId) errors.push('A connection is required for a Database export.');
        break;
      case 'FileExport':
        if (!p.transferFormat) errors.push('A transfer format is required.');
        if (!p.connectionId) errors.push('A connection is required for a File export.');
        if (p.transferFormat === 'CSV' && !p.partnerCode?.trim()) errors.push('Partner code is required when the transfer format is CSV.');
        if (p.transferFormat === 'CSV' && p.useDirectExchange) {
          if (!p.tierDatabaseName?.trim()) errors.push('Tier database name is required when Direct Exchange (legacy) is enabled.');
          const recipients = p.autoLoadRecipients.map(e => e.trim()).filter(e => e.length > 0);
          if (recipients.length > 0 && !recipients.every(e => this.isValidEmail(e))) errors.push('One or more auto-load recipient emails are invalid.');
        }
        break;
    }
    return errors;
  }

  private serializeExport(feed: EditableFeed): void {
    const p = feed.payload;
    if (!feed.exportName || !p) { feed.exportPayload = undefined; return; }

    let payload: Record<string, unknown>;
    switch (feed.exportName) {
      case 'JMA':
        payload = {
          DestEmails: p.destEmails.map(e => e.trim()).filter(e => e.length > 0),
          TemplatePath: p.templatePath.trim()
        };
        break;
      case 'Database':
        payload = {
          AutoLoadSchema: p.autoLoadSchema,
          ConnectionId: p.connectionId,
          UseLegacyIds: p.useLegacyIds
        };
        break;
      case 'FileExport': {
        // Direct Exchange (legacy) only applies to CSV, and its own sub-fields only apply when it's on.
        const useDirectExchange = p.transferFormat === 'CSV' && p.useDirectExchange;
        const recipients = p.autoLoadRecipients.map(e => e.trim()).filter(e => e.length > 0);
        payload = {
          TransferFormat: p.transferFormat,
          ConnectionId: p.connectionId,
          // Partner code only applies to CSV.
          PartnerCode: p.transferFormat === 'CSV' ? (p.partnerCode?.trim() || null) : null,
          UseLegacySync: p.useLegacySync,
          UseLegacyIds: p.useLegacyIds,
          UseDirectExchange: useDirectExchange,
          TierDatabaseName: useDirectExchange ? (p.tierDatabaseName?.trim() || null) : null,
          AutoLoadXref: useDirectExchange && p.autoLoadXref,
          AutoLoadRecipients: useDirectExchange && recipients.length > 0 ? recipients : null
        };
        break;
      }
      default:
        feed.exportPayload = undefined;
        return;
    }
    feed.exportPayload = JSON.stringify(payload);
  }

  addEmail(feed: EditableFeed): void {
    feed.payload?.destEmails.push('');
  }

  removeEmail(feed: EditableFeed, index: number): void {
    feed.payload?.destEmails.splice(index, 1);
    if (feed.payload && feed.payload.destEmails.length === 0) feed.payload.destEmails.push('');
  }

  addRecipient(feed: EditableFeed): void {
    feed.payload?.autoLoadRecipients.push('');
  }

  removeRecipient(feed: EditableFeed, index: number): void {
    feed.payload?.autoLoadRecipients.splice(index, 1);
    if (feed.payload && feed.payload.autoLoadRecipients.length === 0) feed.payload.autoLoadRecipients.push('');
  }

  trackByIndex(index: number): number {
    return index;
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  private isValidWindowsPath(path: string): boolean {
    // Drive-letter path (C:\...) or UNC path (\\server\share\...).
    return /^[a-zA-Z]:\\/.test(path) || /^\\\\[^\\]+\\/.test(path);
  }
}
